import * as React from 'react';
import clsx from 'clsx';

import { dateToDateString } from 'src/tools/date.tools';
import { BaseComponentProps, Dropdown } from 'src/components/Dropdown';
import { InputWithIcons } from 'src/components/Inputs';
import { ifTrue } from 'src/tools/logic.tools';
import { useContext } from 'react';
import { InputContext } from 'src/contexts/input.context';
import { mockFn } from 'src/tools/fn.tools';
import { DateCalendar } from 'src/components/DateCalendar';
import { Factory } from 'src/components/Factory';

import classes from './DateCalendarDropdown.module.scss';

export interface DateCalendarDropdownInputProps extends BaseComponentProps {
  onSubmit: () => void;
  onDiscard: () => void;
  onClick: () => void;
  value: string;
}

interface Props {
  renderInput?: (props: DateCalendarDropdownInputProps) => JSX.Element;
}

export function DateCalendarDropdown({ renderInput }: Props) {
  const {
    classNames,
    value,
    onChange,
    onDiscard = mockFn,
    onSubmit = mockFn,
    min,
    max,
    isDisabled,
    isReadonly,
    isAutoFocus,
    onClick,
    placeholder,
    customization = {},
    initialState,
  } = useContext(InputContext);

  const { dropdownMinWidth = 0 } = customization;

  const [isOpen, setIsOpen] = React.useState(false);

  function handleDatePick(value: Date) {
    setIsOpen(false);
    onChange(value, true);
  }

  function handleOnRequestOpenClose(value: boolean) {
    setIsOpen(value);

    if (!value) onDiscard();
  }

  function handleOnDiscard() {
    if (!isReadonly) handleOnRequestOpenClose(false);
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnSubmit() {
    setIsOpen(false);

    onSubmit();
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const extendedProps = {
      value: ifTrue(typeof value !== 'undefined', dateToDateString(value), ''),
      onDiscard: handleOnDiscard,
      onSubmit: handleOnSubmit,
      onClick: getHandleOnClick(baseComponentProps.onClick),
    };

    if (renderInput)
      return renderInput({
        ...baseComponentProps,
        ...extendedProps,
      });

    return (
      <InputWithIcons
        {...extendedProps}
        className={clsx(ifTrue(isOpen, classes['highlight-input']), classNames?.input)}
        placeholder={placeholder}
        onFocus={baseComponentProps.onFocus}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        rightIconId={baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down'}
        readOnly
      />
    );
  }

  return (
    <Dropdown
      classNames={classNames}
      isDisabled={isDisabled || isReadonly}
      isOpen={!isDisabled && isOpen}
      onRequestOpenClose={handleOnRequestOpenClose}
      renderBaseComponent={renderBaseComponent}
      dropdownMinWidth={dropdownMinWidth}
    >
      <Factory>
        <DateCalendar
          value={value}
          onChange={handleDatePick}
          min={min}
          max={max}
          startDate={initialState?.start_date}
        />
      </Factory>
    </Dropdown>
  );
}
